'use client';

import { useCallback, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useFormState } from 'react-dom';
import { v4 as uuid } from 'uuid';
import { useTranslations } from 'next-intl';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import useLocations, {
  Coordinates
} from 'app/[lang]/[esite]/(core)/_header/hooks/use-locations';
import { DrawerBody, useDrawer } from 'components/pcc-components';
import { DealerLocatorHeader } from './dealer-locator-header';
import { DealerLocatorSearchResults } from './dealer-locator-search-results';
import { DealerLocatorOptionsWrapper } from './dealer-locator-options-wrapper';
import { DealerLocatorSearchForm } from './dealer-locator-search-form';
import { DealerLocatorFiltersDrawer } from './dealer-locator-filters-drawer';
import { useDealerLocatorDrawer } from '../dealer-locator-drawer-context';
import { DealerLocatorFooter } from './dealer-locator-footer';
import {
  DEALER_NAME,
  DEFAULT_NUM_RESULTS,
  DISTANCE,
  OPTIONS,
  SELECTED_STORE_ID,
  SELECT_STORE,
  SORT_ORDER
} from './constants';
import { DEALER_LOCATOR_FORM_ID } from '@cat-ecom/pcc-components/src/components/dealer-locator/constants';
import { SELECT_STORE_INITIAL_STATE } from 'services/dealer-locator/constants';
import {
  Navigation,
  SelectStoreResponse,
  SelectedStore
} from 'services/dealer-locator/types';
import { selectStore } from 'services/dealer-locator/actions';
import { getFilteredAndOrderedDealerStores } from './utils';
import { StoreAddress } from '../types';
import { useHideMaps } from 'hooks/use-hide-maps';
import { useParams, usePathname, useSearchParams } from 'next/navigation';
import {
  Conditional,
  DealerLocatorStoreDetailsDrawer,
  DealerStore,
  useBreakpoint
} from '@cat-ecom/pcc-components';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { useDealerLocatorLoader } from '../dealer-locator-loader-context';
import cx from 'classnames';
import styles from './dealer-locator-content.module.scss';
import DealerLocatorTogglesAndFilter from './dealer-locator-toggle-and-filter/dealer-locator-toggle-filter';
import { setSessionStorage } from '@cat-ecom/common/utils/storageUtils';
import { CONSENT, IS_GUEST } from 'constants/storageConstants';
import { DealerLocatorStoreDetailsDrawerContent } from './dealer-locator-store-details-drawer-content';
import { OrderItemSavedList } from 'services/savedLists/types';
import { useFeatureFlagQuery } from 'services/featureFlag/useFeatureFlagQuery';
import { PCC_STORE_LOCATION_LIST_VIEW_UPDATE } from 'constants/featureFlag';

interface DealerLocatorContentProps {
  autoSearch?: boolean;
  isADP?: boolean;
  isModal?: boolean;
  isRegistration?: boolean;
  storeAddress?: StoreAddress;
  isWelcomeModal?: boolean;
  restrictedDealerStores?: string[];
  isCatCSR?: boolean;
  redirectionUrl?: string;
  savedList?: OrderItemSavedList[];
}

export const DealerLocatorContent = ({
  autoSearch,
  isADP,
  isModal = false,
  isRegistration = false,
  storeAddress,
  isWelcomeModal,
  restrictedDealerStores,
  isCatCSR = false,
  redirectionUrl = '',
  savedList = []
}: DealerLocatorContentProps) => {
  const t = useTranslations();
  const { isHidingMaps } = useHideMaps();
  const dealerLocatorDrawer = useDealerLocatorDrawer();
  const { isDealerLocatorLoading } = useDealerLocatorLoader();

  const sortingOptions = useMemo(
    () => [
      { label: t('FYD_SORT_CLOSEST'), value: DISTANCE },
      { label: t('CAT_EMAIL_DEALERNAME'), value: DEALER_NAME }
    ],
    [t]
  );
  const storeFormMethods = useForm();
  const { fireEvent } = useFormSubmitEvent();

  const defaultFilterOptionsValues: boolean[] = useMemo(
    () => Array(10).fill(false),
    []
  );

  const filterMethods = useForm({
    defaultValues: {
      options: defaultFilterOptionsValues,
      sortOrder: sortingOptions[0]
    }
  });
  const filtersDrawer = useDrawer({
    id: `${
      dealerLocatorDrawer?.drawerProps?.id || 'blocks-dealer-locator-drawer'
    }-filters`,
    onClose: () => {
      shouldCloseEntireDL && dealerLocatorDrawer?.closeDrawer();
    },
    //@ts-expect-error ignoring the 'md' size error, as we need this size for the filters modal if used within our DL modal
    size: isModal ? 'md' : 'lg'
  });
  const storeDetailsDrawer = useDrawer({
    id: `${
      dealerLocatorDrawer?.drawerProps?.id || 'blocks-dealer-locator-drawer'
    }--store-details`,
    size: 'lg'
  });
  const {
    getLocations,
    locations,
    showInCountryToggle,
    unitOfMeasure,
    isLoading,
    isFetching,
    isIdle,
    reset,
    isError,
    isExtendMapToggleChecked,
    setIsExtendMapToggleChecked
  } = useLocations(restrictedDealerStores ?? []);
  const [selectStoreState, selectStoreAction] = useFormState<
    SelectStoreResponse,
    SelectedStore
  >(selectStore, SELECT_STORE_INITIAL_STATE);
  /*  TODO - Input Validations - useSearchParams:The search param object should contain key value pairs where
  the value is of string type, not including reservered url special / unsafe characters like / \ & ? = : % + ' " ; < > { } [ ] @ #
  */
  const queryParams = useSearchParams();

  /*
    TODO - Input Validations - usePathName: string except the
    generalRestrictedCharacters (@\constants\securityConstants.ts)
  */
  const pathname = usePathname();
  const params = useParams();
  const hasQueryParams = !!queryParams.size;
  const allParams = queryParams.toString();
  const showOptions = !!locations?.length || isLoading;

  const [location, setLocation] = useState('');
  const [numDisplayed, setNumDisplayed] = useState(DEFAULT_NUM_RESULTS);
  const [isMapToggleChecked, setIsMapToggleChecked] = useState(false);
  const [filters, setFilters] = useState(null);
  const [areFiltersLoading, setAreFiltersLoading] = useState(false);
  const [shouldCloseEntireDL, setShouldCloseEntireDL] = useState(true);
  const [isSelectStoreError, setIsSelectStoreError] = useState(false);
  const [selectedStoreObjDrawer, setSelectedStoreObjDrawer] =
    useState<DealerStore>();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const {
    filteredAndOrderedVisibleDealerStores,
    allFilteredDealerStoresByDistance
  } = useMemo(
    () => getFilteredAndOrderedDealerStores(locations, numDisplayed, filters),
    [locations, numDisplayed, filters]
  );

  const {
    isStoreLocationTandCFlag = false,
    isfullMapViewMobileFlagEnabled: isFullMapFeatureFlag = false,
    isStoreLocationPinEnhancementFlag = false,
    isStoreLocationMapViewUpdateFlag = false,
    isStoreLocationOptimizationFlag = false
  } = dealerLocatorDrawer || {};

  const { data: flag = {} } = useFeatureFlagQuery(
    PCC_STORE_LOCATION_LIST_VIEW_UPDATE,
    {
      kind: 'location',
      key: params.esite,
      esite: params.esite
    }
  );

  const isStoreLocationListViewUpdateFlag =
    flag?.[PCC_STORE_LOCATION_LIST_VIEW_UPDATE] ?? false;

  const showFilterErrorMessage =
    locations?.length && !filteredAndOrderedVisibleDealerStores.length;
  const showNoStoresMapErrorMessage =
    !isIdle && !isLoading && !locations?.length && !isError;

  const filtersCounter = Object.values(filters || {}).filter(
    (filterValue) => filterValue === true
  ).length;

  const showNetworkErrorMessage = !isIdle && !isLoading && isError;

  const selectedId = storeFormMethods.getValues(SELECTED_STORE_ID);
  const selectedStore = locations?.find(
    ({ id }) => id.toString() === selectedId
  );
  const navigation: Navigation = {
    pathname,
    hasQueryParams,
    queryParams: allParams
  };

  const selectedStoreWithRegistration = {
    ...selectedStore,
    isRegistration,
    navigation,
    savedList
  };

  const selectStoreWithParams = selectStoreAction.bind(
    null,
    selectedStoreWithRegistration
  );

  const formId = `${uuid()}-${DEALER_LOCATOR_FORM_ID}`;

  const handleSubmit = () => {
    if (isCatCSR) {
      setSessionStorage(IS_GUEST, 'true');
    }
    setSessionStorage(CONSENT, 'true');
    fireEvent({
      formName: SELECT_STORE,
      formContent: `${selectedStore?.dealerName}, ${selectedStore?.name}`
    });
  };

  const onSearchThisAreaClick = (center: Coordinates) => {
    storeFormMethods.resetField(SELECTED_STORE_ID);
    setNumDisplayed(DEFAULT_NUM_RESULTS);
    const locationText = `${center.lat}, ${center.lng}`;
    setLocation(locationText);
    getLocations(locationText);
  };

  const resetLocationsAndMapToggle = useCallback(() => {
    reset();
    setIsMapToggleChecked(false);
    setIsExtendMapToggleChecked(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  const resetFilters = useCallback(() => {
    setFilters(null);
    setAreFiltersLoading(false);
    filterMethods.resetField(SORT_ORDER);
    filterMethods.resetField(OPTIONS);
  }, [filterMethods]);

  const resetStoreId = () => storeFormMethods.resetField(SELECTED_STORE_ID);

  const handleMapToggle = () => {
    setIsMapToggleChecked(!isMapToggleChecked);
  };

  const onBackButtonClick = () => {
    storeDetailsDrawer.closeDrawer();
  };

  const isSearchingForLocations = isLoading || isFetching;

  const dealerLocatorSearchForm = () => {
    return (
      <DealerLocatorSearchForm
        location={location}
        setLocation={setLocation}
        getLocations={getLocations}
        setNumDisplayed={setNumDisplayed}
        autoSearch={autoSearch}
        isSearchLoading={isLoading}
        isModal={isModal}
        resetLocationsAndMapToggle={resetLocationsAndMapToggle}
        resetFilters={resetFilters}
        setIsSelectStoreError={setIsSelectStoreError}
        storeAddress={storeAddress}
        isMobile={!isMD}
        isStoreLocationMapViewUpdateFlag={isStoreLocationMapViewUpdateFlag}
        isCatCSR={isCatCSR}
      />
    );
  };

  const dealerLocatorOptionWrapper = () => {
    return (
      <DealerLocatorOptionsWrapper
        locations={locations}
        showInCountryToggle={showInCountryToggle}
        onSearchThisAreaClick={onSearchThisAreaClick}
        isMapToggleChecked={isMapToggleChecked}
        setIsMapToggleChecked={setIsMapToggleChecked}
        isExtendMapToggleChecked={isExtendMapToggleChecked}
        setIsExtendMapToggleChecked={setIsExtendMapToggleChecked}
        isLoading={isSearchingForLocations}
        filters={filters}
        areFiltersLoading={areFiltersLoading}
        setAreFiltersLoading={setAreFiltersLoading}
        isGetLocationsError={isError}
        showNoStoresMapErrorMessage={showNoStoresMapErrorMessage}
        showNetworkErrorMessage={showNetworkErrorMessage}
        setShouldCloseEntireDL={setShouldCloseEntireDL}
        filtersDrawer={filtersDrawer}
        allFilteredDealerStoresByDistance={allFilteredDealerStoresByDistance}
        filteredAndOrderedVisibleDealerStores={
          filteredAndOrderedVisibleDealerStores
        }
        isHidingMaps={isHidingMaps}
        isStoreLocationListViewUpdateFlag={isStoreLocationListViewUpdateFlag}
        isStoreLocationPinEnhancementFlag={isStoreLocationPinEnhancementFlag}
        isStoreLocationMapViewUpdateFlag={isStoreLocationMapViewUpdateFlag}
        isFullMapMobileFeatureFlag={isFullMapFeatureFlag}
      />
    );
  };

  const dealerLocatorSearchResult = () => {
    return (
      <DealerLocatorSearchResults
        numDisplayed={numDisplayed}
        setNumDisplayed={setNumDisplayed}
        unitOfMeasure={unitOfMeasure}
        isMapToggleChecked={isMapToggleChecked}
        isLoading={isSearchingForLocations}
        selectStoreState={selectStoreState}
        showFilterErrorMessage={showFilterErrorMessage}
        showNoStoresMapErrorMessage={
          showNoStoresMapErrorMessage && !isMapToggleChecked
        }
        showNetworkErrorMessage={showNetworkErrorMessage && !isMapToggleChecked}
        allFilteredDealerStoresByDistance={allFilteredDealerStoresByDistance}
        filteredAndOrderedVisibleDealerStores={
          filteredAndOrderedVisibleDealerStores
        }
        isSelectStoreError={isSelectStoreError}
        isStoreLocationTandCFlag={isStoreLocationTandCFlag}
        setIsSelectStoreError={setIsSelectStoreError}
        selectStoreAction={selectStoreAction}
        storeDetailsDrawer={storeDetailsDrawer}
        setselectedStoreObjDrawer={setSelectedStoreObjDrawer}
      />
    );
  };

  const dealerLocatorToggleAndFilterContainer = () => {
    const storeFeatureFlag = {
      isStoreLocationMapViewUpdateFlag,
      isStoreLocationListViewUpdateFlag
    };
    return (
      <DealerLocatorTogglesAndFilter
        showOptions={showOptions}
        isHidingMaps={isHidingMaps}
        isMapToggleChecked={isMapToggleChecked}
        handleMapToggle={handleMapToggle}
        isDealerLocatorLoading={isDealerLocatorLoading}
        filtersDrawer={filtersDrawer}
        filtersCounter={filtersCounter}
        {...storeFeatureFlag}
      ></DealerLocatorTogglesAndFilter>
    );
  };

  return (
    <>
      <FormProvider {...storeFormMethods}>
        <DealerLocatorHeader
          isRegistration={isRegistration}
          isADP={isADP}
          isWelcomeModal={isWelcomeModal}
          isStoreLocationMapViewUpdateFlag={isStoreLocationMapViewUpdateFlag}
          redirectionUrl={redirectionUrl}
        />

        <Conditional
          test={isFullMapFeatureFlag && !isMD}
          fallback={
            <DrawerBody>
              <div
                data-testid="dealer-locator-container-desktop"
                className="px-1"
                style={{ height: '125px' }}
              >
                {dealerLocatorSearchForm()}
                {dealerLocatorOptionWrapper()}
              </div>
              <form
                action={selectStoreWithParams}
                id={formId}
                onSubmit={handleSubmit}
              >
                {dealerLocatorSearchResult()}
              </form>
            </DrawerBody>
          }
        >
          <div
            data-testid="dealer-locator-container-mobile"
            style={{ position: 'relative' }}
          >
            <div
              className={cx(
                styles.baseStyles,

                {
                  'position-absolute': !isHidingMaps && isMapToggleChecked,
                  'position-relative': !(!isHidingMaps && isMapToggleChecked)
                },

                'px-2'
              )}
            >
              {dealerLocatorSearchForm()}
              {dealerLocatorToggleAndFilterContainer()}
            </div>
            <DrawerBody>
              <div>{dealerLocatorOptionWrapper()}</div>
            </DrawerBody>

            <form
              action={selectStoreWithParams}
              id={formId}
              onSubmit={handleSubmit}
            >
              {dealerLocatorSearchResult()}
            </form>
          </div>
        </Conditional>

        <DealerLocatorFooter
          hasSearchedForLocations={!isIdle}
          isSearchingForLocations={isSearchingForLocations}
          isStoreLocationTandCFlag={isStoreLocationTandCFlag}
          showNoStoresMapErrorMessage={showNoStoresMapErrorMessage}
          showNetworkErrorMessage={showNetworkErrorMessage}
          isModal={isModal}
          formId={formId}
          locations={locations}
          isSelectStoreError={isSelectStoreError}
          isWelcomeModal={isWelcomeModal}
          selectStoreState={selectStoreState}
          showInCountryToggle={showInCountryToggle}
          isExtendMapToggleChecked={isExtendMapToggleChecked}
          setIsExtendMapToggleChecked={setIsExtendMapToggleChecked}
          isStoreLocationListViewUpdateFlag={isStoreLocationListViewUpdateFlag}
          isCatCSR={isCatCSR}
        />
        <DealerLocatorStoreDetailsDrawer
          storeDetailsDrawer={storeDetailsDrawer}
        >
          <DealerLocatorStoreDetailsDrawerContent
            selectedStoreObjDrawer={selectedStoreObjDrawer}
            onBackButtonClick={onBackButtonClick}
            unitOfMeasure={unitOfMeasure}
            closestLabel={t('FYD_RESULTS_CLOSEST')}
            isStoreLocationListViewUpdateFlag={
              isStoreLocationListViewUpdateFlag
            }
            isStoreLocationMapViewUpdateFlag={isStoreLocationMapViewUpdateFlag}
            isStoreLocationOptimizationFlag={isStoreLocationOptimizationFlag}
          />
          <DealerLocatorFooter
            hasSearchedForLocations={!isIdle}
            isSearchingForLocations={isSearchingForLocations}
            isStoreLocationTandCFlag={isStoreLocationTandCFlag}
            showNoStoresMapErrorMessage={showNoStoresMapErrorMessage}
            showNetworkErrorMessage={showNetworkErrorMessage}
            isModal={false}
            formId={formId}
            locations={locations}
            isSelectStoreError={isSelectStoreError}
            isWelcomeModal={false}
            selectStoreState={selectStoreState}
            showInCountryToggle={false}
            isExtendMapToggleChecked={isExtendMapToggleChecked}
            setIsExtendMapToggleChecked={setIsExtendMapToggleChecked}
            isStoreLocationListViewUpdateFlag={
              isStoreLocationListViewUpdateFlag
            }
          />
        </DealerLocatorStoreDetailsDrawer>
      </FormProvider>
      <FormProvider {...filterMethods}>
        <DealerLocatorFiltersDrawer
          defaultFilterOptionsValues={defaultFilterOptionsValues}
          filtersDrawer={filtersDrawer}
          filters={filters}
          resetStoreId={resetStoreId}
          setAreFiltersLoading={setAreFiltersLoading}
          setFilters={setFilters}
          setShouldCloseEntireDL={setShouldCloseEntireDL}
          showFilterErrorMessage={showFilterErrorMessage}
          sortingOptions={sortingOptions}
        />
      </FormProvider>
    </>
  );
};
